"use client";

import { useCallback, useEffect, useRef } from "react";

import { toEditableProfile } from "../../personalization.utils";

import { EMAIL_PATTERN } from "../../personalization.constants";

import type { CandidateApplyFitInput } from "../../personalization.types";
export function useCandidateApplyFit({
  controller,
  job,
  skipFitCheck,
  uploadSource,
  initialStep,
  setStep,
  setVerificationEmail,
  setFitAttemptId,
  completeApply,
}: CandidateApplyFitInput) {
  const existingProfileFitStarted = useRef(false);
  const checkApplyFit = useCallback(
    async (profile: ReturnType<typeof toEditableProfile>) => {
      if (skipFitCheck) {
        // Collect-only gate: never evaluate job fit. Redirect once we have an
        // email — instantly for a verified returning candidate, after OTP for a
        // first-time candidate whose resume had no email.
        if (controller.isAuthenticated && EMAIL_PATTERN.test(profile.email)) {
          await completeApply();
          return;
        }
        setVerificationEmail(profile.email);
        try {
          const result = await controller.requestApplyFitVerification(profile, {
            source: "apply_gate",
            skipFit: true,
            ...(job ? { job } : {}),
          });
          if (result.verificationRequired) {
            setStep("otp");
            return;
          }
          await completeApply();
        } catch {
          // The resume is already stored; a failed OTP request must not cost
          // the click — redirect anyway.
          await completeApply();
        }
        return;
      }
      setVerificationEmail(profile.email);
      try {
        const result = await controller.requestApplyFitVerification(profile, {
          source: "apply_gate",
          ...(job ? { job } : {}),
        });
        if (result.applyGate?.fitAttemptId) {
          setFitAttemptId(result.applyGate.fitAttemptId);
        }
        if (result.applyGate?.status === "no_match") {
          if (result.verificationRequired) {
            setStep("otp");
            return;
          }
          setStep("no_match");
          return;
        }
        if (result.applyGate?.status === "unavailable") {
          if (result.verificationRequired) {
            setStep("otp");
            return;
          }
          setStep("unavailable");
          return;
        }
        if (result.applyGate?.status === "match") {
          if (result.verificationRequired) {
            setStep("otp");
            return;
          }
          await completeApply();
          return;
        }
        setStep("unavailable");
      } catch {
        setStep("unavailable");
      }
    },
    [completeApply, controller, job, skipFitCheck],
  );

  useEffect(() => {
    if (
      uploadSource !== "apply_gate" ||
      initialStep !== "parsing" ||
      !controller.isAuthenticated ||
      !controller.editableProfile ||
      existingProfileFitStarted.current
    ) {
      return;
    }
    existingProfileFitStarted.current = true;
    void checkApplyFit(controller.editableProfile);
  }, [
    checkApplyFit,
    controller.editableProfile,
    controller.isAuthenticated,
    initialStep,
    uploadSource,
  ]);

  return checkApplyFit;
}
